import http from './http';

const ENDPOINTS = {
  USERS: '/api/v1/admin/users',
  USER_BY_ID: (id) => `/api/v1/admin/users/${id}`,
  USER_STATS: '/api/v1/admin/users/stats',
  USER_STATUS: (id) => `/api/v1/admin/users/${id}/status`,
};

/**
 * User API Service (Admin endpoints)
 */
export const userAPI = {
  // Create a new user
  create: async (userData) => {
    const response = await http.post(ENDPOINTS.USERS, userData);
    return response.data;
  },

  // Get user by ID
  getById: async (id) => {
    const response = await http.get(ENDPOINTS.USER_BY_ID(id));
    return response.data;
  },

  // Update user
  update: async (id, userData) => {
    const response = await http.put(ENDPOINTS.USER_BY_ID(id), userData);
    return response.data;
  },

  // List users with filtering
  list: async (params = {}) => {
    const response = await http.get(ENDPOINTS.USERS, { params });
    return response.data;
  },

  // Get all doctors
  getDoctors: async () => {
    const response = await http.get(ENDPOINTS.USERS, {
      params: { 
        role: 'Doctor',
        status: 'active',
        limit: 100 // Get all active doctors
      }
    });
    return response.data;
  },

  // Get all users by role
  getByRole: async (role) => {
    const response = await http.get(ENDPOINTS.USERS, {
      params: { 
        role: role,
        status: 'active',
        limit: 100
      }
    });
    return response.data;
  },

  // Search users
  search: async (searchTerm, role = null) => {
    const params = { search: searchTerm };
    if (role) params.role = role;
    
    const response = await http.get(ENDPOINTS.USERS, { params });
    return response.data;
  },

  // Get user statistics
  getStats: async () => {
    const response = await http.get(ENDPOINTS.USER_STATS);
    return response.data;
  },

  // Update user status
  updateStatus: async (id, status) => {
    const response = await http.patch(ENDPOINTS.USER_STATUS(id), { status });
    return response.data;
  },

  // Delete user
  delete: async (id) => {
    const response = await http.delete(ENDPOINTS.USER_BY_ID(id));
    return response.data;
  }
};

// Profile API Service
class ProfileService {
  constructor() {
    this.endpoint = '/api/v1/profile';
  }

  async getProfile() {
    const response = await http.get(this.endpoint);
    return response.data?.data ?? response.data;
  }

  async updateProfile(data) {
    const response = await http.put(this.endpoint, data);
    return response.data?.data ?? response.data;
  }

  async changePassword(data) {
    const response = await http.put(this.endpoint, data);
    return response.data?.data ?? response.data;
  }

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const response = await http.put(this.endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data?.data ?? response.data;
  }

  async deleteAvatar() {
    const response = await http.delete(this.endpoint);
    return response.data?.data ?? response.data;
  }

  async updateConsultationFees(data) {
    // Accepts any combination of: consultationFees, followUpFees, followUpValidityDays
    const response = await http.put(`${this.endpoint}/consultation-fees`, data);
    return response.data?.data ?? response.data;
  }

  async getConsultationFees() {
    const response = await http.get(`${this.endpoint}/consultation-fees`);
    const raw = response.data?.data ?? response.data;
    if (!raw) return raw;
    // Expected shape: { doctorInfo: {...}, fees: { consultationFees, followUpFees, followUpValidityDays } }
    if (raw.fees || raw.doctorInfo) {
      return {
        doctorInfo: raw.doctorInfo || null,
        consultationFees: raw.fees?.consultationFees ?? raw.consultationFees,
        followUpFees: raw.fees?.followUpFees ?? raw.followUpFees,
        followUpValidityDays: raw.fees?.followUpValidityDays ?? raw.followUpValidityDays
      };
    }
    return raw;
  }
}

// Export profile service instance
export const profileService = new ProfileService();

// Gender options
export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

// Relationship options for emergency contact
export const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'other', label: 'Other' }
];

// Language options
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'mr', label: 'Marathi' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' }
];

// Theme options
export const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' }
];

// Export the userAPI as the default export for backward compatibility
export default userAPI;
